import { PropsWithChildren, Suspense } from 'react';

// eslint-disable-next-line no-restricted-imports -- Root level import allowed
import { Toast } from '@opendoor/bricks/complex/Toast';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { AddContextualData } from '@opendoor/observability';
import { useLocation } from '@remix-run/react';

import { useBrowserCompatibilityData } from 'shared/helpers/globals';
import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';
import { usePath } from 'shared/helpers/hooks/usePath';

import { LoadingPage } from 'platform/components/LoadingPage';
import { TOSModal, TOSProvider } from 'platform/components/TOSProvider';
import { InternalNavA11yAnnouncer } from 'platform/InternalNavA11yAnnouncer';
import { ODTeamBoundary } from 'platform/ODTeamBoundary';
import { ClientOnly } from 'platform/remix-run/utils/client-only';

/**
 * Adds route and device contextual data to the observability context.
 */
const RouteContextualData: React.FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const path = usePath();
  const { isMobile } = useMobileDevice();
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  return (
    <AddContextualData
      data={{ path, pathname, isMobile, isMobileNativeWebView: isOpendoorAppEvoWebView }}
    >
      {children}
    </AddContextualData>
  );
};

export const DashboardLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const path = usePath();
  return (
    <Suspense fallback={<LoadingPage from={`App-${path}`} reason="suspense" />}>
      <ODTeamBoundary team="consumer-platform">
        <RouteContextualData>
          <InternalNavA11yAnnouncer />
          <ThemedLite theme="eero">
            <TOSProvider>
              {children}
              {/* NOTE: TOSModal is rendered ONLY on the client since Popover cannot interpret the theme provider correctly when rendered on the server */}
              <ClientOnly>{() => <TOSModal />}</ClientOnly>
            </TOSProvider>
          </ThemedLite>
          {/* Toast should only be mounted once at the root */}
          <Toast />
        </RouteContextualData>
      </ODTeamBoundary>
    </Suspense>
  );
};
